import {
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

// Feature 42 — VIX / VXX meter, from GET /market/vol-meter. Historical frequencies only:
// the research found no way to time spikes well enough to pay for VXX's decay.

const LEVEL_COLORS: Record<string, string> = {
  calm: '#2E7D32',
  nervous: '#9E9D24',
  stressed: '#EF6C00',
  crazy: '#C62828',
  panic: '#6A1B9A',
};
const LEVEL_RANGE: Record<string, string> = {
  calm: '< 20', nervous: '20–25', stressed: '25–30', crazy: '30–40', panic: '40+',
};
const STAGE_NAMES: Record<string, string> = {
  calm: 'Calm (no spike)', starting: 'Spike starting (days 1–3)', under_way: 'Spike under way (days 4–11)', late: 'Spike fading (day 12+)',
};

const pct = (v: number | null | undefined, digits = 0) =>
  v == null ? '—' : `${(v * 100).toFixed(digits)}%`;
const signed = (v: number | null | undefined, digits = 1) =>
  v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(digits)}%`;

function LevelGauge({ levels, current }: { levels: string[]; current: string }) {
  const idx = Math.max(0, levels.indexOf(current));
  const cx = 120, cy = 110, r = 90, w = 22, n = levels.length;
  const arc = (i: number) => {
    const a0 = Math.PI - (i * Math.PI) / n, a1 = Math.PI - ((i + 1) * Math.PI) / n;
    const p = (a: number, rr: number) => `${cx + rr * Math.cos(a)} ${cy - rr * Math.sin(a)}`;
    return `M ${p(a0, r)} A ${r} ${r} 0 0 1 ${p(a1, r)} L ${p(a1, r - w)} A ${r - w} ${r - w} 0 0 0 ${p(a0, r - w)} Z`;
  };
  const needle = Math.PI - ((idx + 0.5) * Math.PI) / n;
  return (
    <svg viewBox="0 0 240 130" width="100%" style={{ maxWidth: 300 }} role="img" aria-label={`VIX level: ${current}`}>
      {levels.map((l, i) => (
        <path key={l} d={arc(i)} fill={LEVEL_COLORS[l]} opacity={i === idx ? 1 : 0.28} />
      ))}
      {levels.map((l, i) => {
        const a = Math.PI - ((i + 0.5) * Math.PI) / n;
        return (
          <text key={l} x={cx + (r + 15) * Math.cos(a)} y={cy - (r + 15) * Math.sin(a) + 4} textAnchor="middle"
                fontSize="10" fontWeight={i === idx ? 800 : 500} fill="currentColor">
            {l}
          </text>
        );
      })}
      <line x1={cx} y1={cy} x2={cx + (r - 30) * Math.cos(needle)} y2={cy - (r - 30) * Math.sin(needle)}
            stroke="currentColor" strokeWidth={4} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={7} fill="currentColor" />
    </svg>
  );
}

export default function VolMeter({ meter }: { meter: any }) {
  if (!meter) return null;
  const color = LEVEL_COLORS[meter.level] ?? '#757575';
  const nm = meter.next_month?.next_month ?? {};
  const sp = meter.spike;
  const verdictColor = meter.verdict?.startsWith('TAKE') ? '#C62828' : meter.verdict?.startsWith('COIN') ? '#EF6C00' : '#455A64';

  return (
    <Paper sx={{ p: 3, mb: 3, borderLeft: `6px solid ${color}` }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4} sx={{ textAlign: 'center', color: 'text.primary' }}>
          <LevelGauge levels={meter.levels ?? []} current={meter.level} />
          <Typography variant="h5" fontWeight={800} sx={{ color }}>
            VIX {meter.vix?.toFixed(1)} · {meter.level?.toUpperCase()}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{meter.stage_text}</Typography>
          <Chip label={meter.verdict} sx={{ mt: 1.5, bgcolor: verdictColor, color: '#FFFFFF', fontWeight: 800 }} />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            VIX / VIX3M {meter.curve?.toFixed(2)} — {meter.curve_text} · VXX ${meter.vxx?.toFixed(2)}
            {' '}({signed(meter.vxx_1m)} 1 month, {signed(meter.vxx_1y, 0)} 1 year) · as of {meter.as_of}
          </Typography>
        </Grid>

        <Grid item xs={12} md={8}>
          <Typography variant="body2" sx={{ mb: 2 }}>{meter.explanation}</Typography>

          {sp && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Day {sp.day} of a spike: VIX rose from {sp.base_vix} to a peak so far of {sp.peak_so_far}.
              {sp.remaining && ` Spikes that had reached this level stayed elevated a median ${sp.remaining.median_days_left} more trading days (a quarter lasted over ${sp.remaining.p75_days_left}).`}
            </Alert>
          )}

          <Typography variant="subtitle2" gutterBottom>
            How crazy next month gets — historical odds when VIX was {meter.next_month?.vix_from}–{meter.next_month?.vix_to}
            {' '}({meter.next_month?.days} days since {meter.since})
          </Typography>
          <Box sx={{ display: 'flex', height: 30, borderRadius: 1, overflow: 'hidden', mb: 0.5 }}>
            {(meter.levels ?? []).map((l: string) => (nm[l] ?? 0) > 0 && (
              <Box key={l} sx={{ width: `${nm[l] * 100}%`, bgcolor: LEVEL_COLORS[l], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {nm[l] >= 0.08 && (
                  <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 700 }}>{pct(nm[l])}</Typography>
                )}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            {(meter.levels ?? []).map((l: string) => (
              <Typography key={l} variant="caption" color="text.secondary">
                <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: LEVEL_COLORS[l], mr: 0.5, borderRadius: 0.3 }} />
                {l} ({LEVEL_RANGE[l]}): {pct(nm[l])}
              </Typography>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            The highest VIX close over the next 21 trading days. Today's VIX was the best forecast found: 2,020 signals and three
            machine-learning models did not beat it (docs/features/42).
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>What VXX did next, by stage (real prices, 2011→)</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Stage</TableCell>
                <TableCell align="right">Next 5 days</TableCell>
                <TableCell align="right">Next 21 days</TableCell>
                <TableCell align="right">Rose (21 d)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(meter.vxx_by_stage ?? []).map((r: any) => (
                <TableRow key={r.stage} selected={r.stage === meter.stage}>
                  <TableCell>{STAGE_NAMES[r.stage] ?? r.stage}</TableCell>
                  <TableCell align="right">{signed(r['5d']?.median)}</TableCell>
                  <TableCell align="right">{signed(r['21d']?.median)}</TableCell>
                  <TableCell align="right">{pct(r['21d']?.win)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Medians. A spike starts when VIX closes 1.5× above its low of the past 21 days and lasts while VIX stays 25% above that low.
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>How long spikes last and what VXX paid at best</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Spike reached</TableCell>
                <TableCell align="right">Spikes</TableCell>
                <TableCell align="right">Days elevated</TableCell>
                <TableCell align="right">VXX from calm</TableCell>
                <TableCell align="right">from trigger</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(meter.durations ?? []).map((r: any) => (
                <TableRow key={r.level}>
                  <TableCell>
                    <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: LEVEL_COLORS[r.level], mr: 0.75, borderRadius: 0.3 }} />
                    {r.level}
                  </TableCell>
                  <TableCell align="right">{r.episodes}</TableCell>
                  <TableCell align="right">{r.median_days_elevated}</TableCell>
                  <TableCell align="right">{signed(r.median_vxx_from_calm, 0)}</TableCell>
                  <TableCell align="right">{signed(r.median_vxx_from_trigger, 0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Medians since 2003, to VXX's best close in each spike — no one sells exactly there. Most spikes are small and over within
            a week; only crazy and panic spikes pay several times VXX's decay, and they look the same as small ones when they start.
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}
